import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { Subscription } from 'rxjs';
import { ReportDropdownCoordinatorService } from './report-dropdown-coordinator.service';

export type ReportSelectValue = string | number | boolean | null | undefined;

export type ReportEnumOption = {
  value: ReportSelectValue;
  label: string;
};

let enumSelectSequence = 0;

@Component({
  standalone: false,
  selector: 'app-report-enum-select',
  templateUrl: './report-enum-select.component.html',
  styleUrls: ['./report-enum-select.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class ReportEnumSelectComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('trigger', { static: true }) trigger?: CdkOverlayOrigin;
  @ViewChild('panel') panel?: ElementRef<HTMLElement>;

  @Input() options: ReportEnumOption[] = [];
  @Input() value: ReportSelectValue;
  @Input() placeholder = 'Choose an option';
  @Input() ariaLabel = 'Choose an option';
  @Input() controlKey = `report-enum-${++enumSelectSequence}`;
  @Input() disabled = false;
  @Input() allowClear = false;
  @Input() invalid = false;
  @Input() searchable = false;
  @Input() searchPlaceholder = 'Search options';
  @Input() noOptionsLabel = 'No options found';

  @Output() readonly valueChange = new EventEmitter<ReportSelectValue>();

  open = false;
  trackOption(_index: number, option: ReportEnumOption): ReportSelectValue {
    return option.value;
  }
  panelWidth = 0;
  query = '';
  activeIndex = -1;

  readonly positions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 6,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -6,
    },
  ];

  private activeControlSubscription?: Subscription;
  private resizeObserver?: ResizeObserver;
  private documentListenersAttached = false;

  private readonly documentPointerDown = (event: PointerEvent): void => {
    if (!this.open || this.eventIsInsideControl(event)) return;
    this.close();
  };

  private readonly documentKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.open) this.handleKeydown(event);
  };

  constructor(private readonly coordinator: ReportDropdownCoordinatorService) {}

  ngOnInit(): void {
    this.activeControlSubscription = this.coordinator.activeControl$.subscribe((activeControl) => {
      this.open = activeControl === this.controlKey;
      if (this.open) {
        this.syncPanelWidth();
        this.attachDocumentListeners();
      } else {
        this.detachDocumentListeners();
      }
    });
  }

  ngAfterViewInit(): void {
    const origin = this.trigger?.elementRef.nativeElement;
    if (!origin || typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => this.syncPanelWidth());
    this.resizeObserver.observe(origin);
    this.syncPanelWidth();
  }

  ngOnDestroy(): void {
    this.coordinator.close(this.controlKey);
    this.detachDocumentListeners();
    this.resizeObserver?.disconnect();
    this.activeControlSubscription?.unsubscribe();
  }

  toggle(): void {
    if (this.disabled) return;
    if (this.open) {
      this.close();
      return;
    }
    this.query = '';
    this.activeIndex = Math.max(
      0,
      this.filteredOptions.findIndex((option) => this.isSelected(option)),
    );
    this.syncPanelWidth();
    this.coordinator.open(this.controlKey);
  }

  handleAttach(): void {
    this.syncPanelWidth();
    setTimeout(() => {
      if (this.searchable) {
        this.panel?.nativeElement.querySelector<HTMLInputElement>('input')?.focus();
      } else {
        this.focusActiveOption();
      }
    });
  }

  close(): void {
    this.coordinator.close(this.controlKey);
  }

  select(option: ReportEnumOption): void {
    this.valueChange.emit(option.value);
    this.close();
  }

  clear(event: Event): void {
    event.stopPropagation();
    this.valueChange.emit(undefined);
    this.close();
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      this.trigger?.elementRef.nativeElement.focus();
      return;
    }
    if (!this.open || !['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter'].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const options = this.filteredOptions;
    if (options.length === 0) return;
    if (event.key === 'Enter') {
      this.select(options[Math.max(0, this.activeIndex)]);
      return;
    }
    if (event.key === 'Home') this.activeIndex = 0;
    else if (event.key === 'End') this.activeIndex = options.length - 1;
    else if (event.key === 'ArrowDown') this.activeIndex = (this.activeIndex + 1) % options.length;
    else this.activeIndex = (this.activeIndex - 1 + options.length) % options.length;
    this.focusActiveOption();
  }

  handleTriggerKeydown(event: KeyboardEvent): void {
    if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    if (!this.open) this.toggle();
  }

  searchChanged(value: string): void {
    this.query = value;
    this.activeIndex = this.filteredOptions.length > 0 ? 0 : -1;
  }

  get selectedLabel(): string {
    return this.options.find((option) => option.value === this.value)?.label ?? '';
  }

  isSelected(option: ReportEnumOption): boolean {
    return option.value === this.value;
  }

  get filteredOptions(): ReportEnumOption[] {
    const query = this.query.trim().toLocaleLowerCase();
    return query
      ? this.options.filter((option) => option.label.toLocaleLowerCase().includes(query))
      : this.options;
  }

  private syncPanelWidth(): void {
    const width = this.trigger?.elementRef.nativeElement.getBoundingClientRect().width ?? 0;
    if (width > 0) this.panelWidth = width;
  }

  private focusActiveOption(): void {
    setTimeout(() => {
      this.panel?.nativeElement
        .querySelector<HTMLButtonElement>(`[data-option-index="${this.activeIndex}"]`)
        ?.focus();
    });
  }

  private eventIsInsideControl(event: Event): boolean {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const trigger = this.trigger?.elementRef.nativeElement;
    const panel = this.panel?.nativeElement;
    const target = event.target as Node | null;
    return !!(
      (trigger && (path.includes(trigger) || (target && trigger.contains(target))))
      || (panel && (path.includes(panel) || (target && panel.contains(target))))
    );
  }

  private attachDocumentListeners(): void {
    if (this.documentListenersAttached) return;
    const documentRef = this.trigger?.elementRef.nativeElement.ownerDocument;
    if (!documentRef) return;
    documentRef.addEventListener('pointerdown', this.documentPointerDown);
    documentRef.addEventListener('keydown', this.documentKeydown);
    this.documentListenersAttached = true;
  }

  private detachDocumentListeners(): void {
    if (!this.documentListenersAttached) return;
    const documentRef = this.trigger?.elementRef.nativeElement.ownerDocument;
    documentRef?.removeEventListener('pointerdown', this.documentPointerDown);
    documentRef?.removeEventListener('keydown', this.documentKeydown);
    this.documentListenersAttached = false;
  }
}
